/**
 * Deterministic float64 collision primitives — the single hit-math module shared by
 * client prediction and server authority. Scalar args (no vector allocation) so they
 * are cheap to call per-projectile per-tick. Coordinates must be in ONE frame (see
 * frames.ts) — resolve hits in the body-LOCAL frame.
 *
 * Each test returns the entry parameter `t` in [0,1] along the swept segment P0->P1
 * (so the impact point is `P0 + t*(P1-P0)`), or `-1` for a miss. Segment sweeping is
 * what prevents fast bolts from tunnelling through thin targets between ticks — at
 * 340 m/s and a 10 Hz tick a bolt covers ~34 m per step, far larger than a hitbox.
 */

const EPS = 1e-12;

/**
 * Swept segment P0->P1 versus a sphere (centre C, radius r). Returns the entry `t`
 * (smallest root in [0,1]) or `-1`. If P0 is already inside the sphere, returns 0.
 *
 * Solves |P0 + t*(P1-P0) - C|^2 = r^2, the exact first-contact of the segment with
 * the sphere surface.
 */
export function segmentSphere(
  p0x: number, p0y: number, p0z: number,
  p1x: number, p1y: number, p1z: number,
  cx: number, cy: number, cz: number,
  r: number,
): number {
  const dx = p1x - p0x, dy = p1y - p0y, dz = p1z - p0z;
  const fx = p0x - cx, fy = p0y - cy, fz = p0z - cz;
  const c = fx * fx + fy * fy + fz * fz - r * r;
  if (c <= 0) return 0; // P0 already inside the sphere
  const a = dx * dx + dy * dy + dz * dz;
  if (a <= EPS) return -1; // zero-length segment, start outside
  const b = 2 * (fx * dx + fy * dy + fz * dz);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  if (t < 0 || t > 1) return -1;
  return t;
}

/**
 * Swept segment P0->P1 versus a capsule (core segment A->B, radius r) — the humanoid
 * hitbox (feet-to-head core). Returns the `t` along P0->P1 of the closest approach
 * when that approach is within `r` (a good impact estimate for hit detection), else
 * `-1`. Uses the standard closest-points-between-two-segments solve.
 */
export function segmentCapsule(
  p0x: number, p0y: number, p0z: number,
  p1x: number, p1y: number, p1z: number,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  r: number,
): number {
  // Segment 1: P0 + s*d1 (the swept bolt).  Segment 2: A + u*d2 (the capsule core).
  const d1x = p1x - p0x, d1y = p1y - p0y, d1z = p1z - p0z;
  const d2x = bx - ax, d2y = by - ay, d2z = bz - az;
  const rx = p0x - ax, ry = p0y - ay, rz = p0z - az;
  const a = d1x * d1x + d1y * d1y + d1z * d1z; // |d1|^2
  const e = d2x * d2x + d2y * d2y + d2z * d2z; // |d2|^2
  const f = d2x * rx + d2y * ry + d2z * rz;

  let s: number;
  let u: number;
  if (a <= EPS && e <= EPS) {
    s = 0; u = 0; // both degenerate to points
  } else if (a <= EPS) {
    s = 0;
    u = clamp01(f / e);
  } else {
    const c = d1x * rx + d1y * ry + d1z * rz;
    if (e <= EPS) {
      u = 0;
      s = clamp01(-c / a);
    } else {
      const b = d1x * d2x + d1y * d2y + d1z * d2z;
      const denom = a * e - b * b; // >= 0
      s = denom > EPS ? clamp01((b * f - c * e) / denom) : 0;
      u = (b * s + f) / e;
      if (u < 0) { u = 0; s = clamp01(-c / a); }
      else if (u > 1) { u = 1; s = clamp01((b - c) / a); }
    }
  }

  // Closest points, then compare their gap to the capsule radius.
  const c1x = p0x + d1x * s, c1y = p0y + d1y * s, c1z = p0z + d1z * s;
  const c2x = ax + d2x * u, c2y = ay + d2y * u, c2z = az + d2z * u;
  const gx = c1x - c2x, gy = c1y - c2y, gz = c1z - c2z;
  if (gx * gx + gy * gy + gz * gz <= r * r) return s;
  return -1;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
